import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert,
  TextField
} from '@mui/material';
import {
  Mail as MailIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import {
  useRecordContext,
  useNotify,
  useDataProvider,
  useGetIdentity
} from 'react-admin';
import { Funeral, CustomerEventType } from '../types';
import { getToken } from '../authentication/authProvider';
import { supabase as supabaseClient } from '../lib/supabaseClient';

interface ClientOption {
  id: number;
  label: string;
  name: string;
  civic_number?: string;
  apartment?: string;
  address: string;
  address2?: string;
  city: string;
  province: string;
  postal_code: string;
}

interface DocumentOption {
  url: string;
  filename: string;
  source: 'contract' | 'attachment' | 'cpp_benefits' | 'verified_document' | 'receipt';
}

interface PhysicalMailButtonProps {
  /** Controlled open state. When provided, the built-in trigger button is hidden
   *  and the parent drives the dialog (used by the standardized ContactCard). */
  open?: boolean;
  onClose?: () => void;
  /** Pre-select this client as the recipient once clients load. */
  initialClientId?: number;
  /** Hide the default "Mail" trigger button (e.g. when opened from a card icon). */
  hideTrigger?: boolean;
}

const PhysicalMailButton = ({ open, onClose, initialClientId, hideTrigger }: PhysicalMailButtonProps = {}) => {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();

  const controlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogOpen = controlled ? !!open : internalOpen;
  const [loading, setLoading] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | ''>('');
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [availableClients, setAvailableClients] = useState<ClientOption[]>([]);
  const [availableDocuments, setAvailableDocuments] = useState<DocumentOption[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<Record<string, number>>({});
  const [fetchingData, setFetchingData] = useState(false);
  const [includeCoverPage, setIncludeCoverPage] = useState(false);
  const [coverPageText, setCoverPageText] = useState('');

  // Fetch available clients when dialog opens
  useEffect(() => {
    const fetchData = async () => {
      if (dialogOpen && record) {
        await fetchAvailableClients();
        await fetchAvailableDocuments();
      }
    };

    void fetchData();
  }, [dialogOpen, record]);

  const fetchAvailableClients = async () => {
    if (!record) return;

    setFetchingData(true);
    try {
      const clientIds = [
        { id: record.deceased_client_id, label: 'Deceased' },
        { id: record.primary_client_id, label: 'Primary Contact' },
        { id: record.paying_client_id, label: 'Paying Client' },
        { id: record.authorized_client_id, label: 'Authorized Person' }
      ].filter(item => item.id !== null);

      const clients: ClientOption[] = [];

      for (const item of clientIds) {
        const { data, error } = await supabaseClient
          .from('clients')
          .select('id, first_name, last_name, civic_number, apartment, address, address2, city, province, postal_code')
          .eq('id', item.id)
          .single();

        if (!error && data) {
          // Only include clients with complete address information
          if (data.address && data.city && data.province && data.postal_code) {
            clients.push({
              id: data.id,
              label: item.label,
              name: `${data.first_name || ''} ${data.last_name || ''}`.trim(),
              civic_number: data.civic_number,
              apartment: data.apartment,
              address: data.address,
              address2: data.address2,
              city: data.city,
              province: data.province,
              postal_code: data.postal_code
            });
          }
        }
      }

      setAvailableClients(clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      notify('Error fetching client information', { type: 'error' });
    } finally {
      setFetchingData(false);
    }
  };

  const fetchAvailableDocuments = async () => {
    if (!record?.id) return;

    try {
      const documents: DocumentOption[] = [];

      // Fetch signed contracts and CPP benefits documents
      const { data: funeral, error: funeralError } = await supabaseClient
        .from('funerals')
        .select('contracts_signed_urls, cpp_benefits_signed_urls')
        .eq('id', record.id)
        .single();

      if (!funeralError && funeral?.contracts_signed_urls) {
        funeral.contracts_signed_urls.forEach((url: string) => {
          const filename = getFilenameFromUrl(url);
          documents.push({
            url,
            filename,
            source: 'contract'
          });
        });
      }

      // Add CPP benefits documents
      if (!funeralError && funeral?.cpp_benefits_signed_urls) {
        funeral.cpp_benefits_signed_urls.forEach((url: string) => {
          const filename = getFilenameFromUrl(url);
          documents.push({
            url,
            filename: filename.includes('CPP') ? filename : `CPP Benefits - ${filename}`,
            source: 'cpp_benefits'
          });
        });
      }

      // Fetch attachments
      const { data: attachments, error: attachmentsError } = await supabaseClient
        .from('attachments')
        .select('path, title')
        .eq('funeral_id', record.id)
        .order('created_at', { ascending: false });

      if (!attachmentsError && attachments) {
        for (const attachment of attachments) {
          // Generate signed URL for the attachment
          const { data: signedData, error: signedError } = await supabaseClient.storage
            .from('attachments')
            .createSignedUrl(attachment.path, 3600); // 1 hour expiry

          if (!signedError && signedData?.signedUrl) {
            documents.push({
              url: signedData.signedUrl,
              filename: attachment.title || getFilenameFromUrl(attachment.path),
              source: 'attachment'
            });
          }
        }
      }

      // Fetch verified documents (POD/POC)
      try {
        const nestJsApiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';
        const token = await getToken();
        const res = await fetch(`${nestJsApiUrl}documents/funeral/${record.id}/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const verifiedDocs = data.verified_documents || [];
          for (const doc of verifiedDocs) {
            if (doc.status === 'valid') {
              const docTypeLabel = doc.document_type === 'proof_of_death'
                ? 'Proof of Death'
                : doc.document_type === 'proof_of_cremation'
                ? 'Proof of Cremation'
                : doc.document_type;
              documents.push({
                url: `${nestJsApiUrl}documents/funeral-document/${doc.id}/download`,
                filename: `${docTypeLabel} (${doc.verification_id})`,
                source: 'verified_document'
              });
            }
          }

          // Cleo Contract Receipt (stored funeral_document) — same download route.
          const funeralDocuments = data.funeral_documents || [];
          for (const doc of funeralDocuments) {
            if (doc.document_type === 'cleo_contract_receipt') {
              documents.push({
                url: `${nestJsApiUrl}documents/funeral-document/${doc.id}/download`,
                filename: doc.original_filename || 'Cleo Contract Receipt.pdf',
                source: 'receipt'
              });
            }
          }
        }
      } catch (err) {
        console.error('Error fetching verified documents:', err);
      }

      setAvailableDocuments(documents);
    } catch (error) {
      console.error('Error fetching documents:', error);
      notify('Error fetching documents', { type: 'error' });
    }
  };

  const getFilenameFromUrl = (url: string): string => {
    try {
      const pathname = new URL(url).pathname;
      const filename = pathname.split('/').pop() || 'Unknown file';
      return decodeURIComponent(filename);
    } catch (error) {
      const parts = url.split('/');
      return parts[parts.length - 1] || 'Unknown file';
    }
  };

  const handleClientChange = (clientId: number) => {
    setSelectedClientId(clientId);
    const client = availableClients.find(c => c.id === clientId);
    setSelectedClient(client || null);
  };

  const handleDocumentToggle = (url: string) => {
    setSelectedDocuments(prev => {
      const next = { ...prev };
      if (url in next) {
        delete next[url];
      } else {
        next[url] = 1;
      }
      return next;
    });
  };

  const handleQuantityChange = (url: string, quantity: number) => {
    const clamped = Math.max(1, Math.min(10, quantity));
    setSelectedDocuments(prev => ({ ...prev, [url]: clamped }));
  };

  const selectedUrls = Object.keys(selectedDocuments);
  const totalCopies = Object.values(selectedDocuments).reduce((sum, q) => sum + q, 0);

  const handleSendMail = async () => {
    if (!selectedClientId || selectedUrls.length === 0) {
      notify('Please select a recipient and at least one document', { type: 'warning' });
      return;
    }

    if (!record?.id) {
      notify('Funeral record not found', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const nestJsApiUrl = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/';
      const token = await getToken();

      const response = await fetch(`${nestJsApiUrl}funerals/${record.id}/send-physical-mail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipientClientId: selectedClientId,
          documentUrls: selectedUrls,
          description: `Documents for ${selectedClient?.name}`,
          ...(includeCoverPage && coverPageText ? { coverPageText } : {}),
          documentQuantities: selectedDocuments
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send physical mail');
      }

      // Create a customer event for tracking
      await dataProvider.create('customer_events', {
        data: {
          funeral_id: Number(record.id),
          timestamp: new Date().toISOString(),
          type: CustomerEventType.MAIL_SENT,
          recipient_type: 'customer',
          recipient_id: Number(selectedClientId),
          recipient_name: selectedClient?.name,
          contact_method: 'physical_mail',
          contact_detail: result.trackingInfo.recipientAddress,
          message: `Sent ${totalCopies} document(s) via PostGrid.${includeCoverPage ? ' Includes cover letter.' : ''} Tracking: ${result.trackingInfo.trackingId}`,
          user_id: identity?.id ? Number(identity.id) : undefined,
          user_name: identity?.fullName || 'System'
        }
      });

      notify(`Physical mail sent successfully to ${selectedClient?.name}`, { type: 'success' });
      handleCloseDialog();
    } catch (error) {
      console.error('Error sending physical mail:', error);
      notify(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setInternalOpen(true);
    setSelectedClientId('');
    setSelectedClient(null);
    setSelectedDocuments({});
    setIncludeCoverPage(false);
    setCoverPageText('');
  };

  const handleCloseDialog = () => {
    if (controlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
    setSelectedClientId('');
    setSelectedClient(null);
    setSelectedDocuments({});
    setIncludeCoverPage(false);
    setCoverPageText('');
  };

  // Pre-select the requested recipient once the address-complete clients load.
  useEffect(() => {
    if (!dialogOpen || initialClientId == null || availableClients.length === 0) return;
    if (selectedClientId !== '') return;
    const match = availableClients.find((c) => c.id === initialClientId);
    if (match) {
      setSelectedClientId(match.id);
      setSelectedClient(match);
    }
  }, [dialogOpen, initialClientId, availableClients, selectedClientId]);

  if (!record) return null;

  return (
    <>
      {!hideTrigger && (
        <Button
          onClick={handleOpenDialog}
          startIcon={<MailIcon />}
          size="medium"
        >
          Mail
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Send Physical Mail</DialogTitle>
        <DialogContent>
          {fetchingData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Cover Page Option */}
              <Box sx={{ mb: 2, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeCoverPage}
                      onChange={(e) => setIncludeCoverPage(e.target.checked)}
                    />
                  }
                  label="Include Cover Page"
                />
                {includeCoverPage && (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Cover Letter Message"
                    placeholder="Enter text for the cover letter..."
                    value={coverPageText}
                    onChange={(e) => setCoverPageText(e.target.value)}
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Client Selection */}
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel id="client-select-label">Select Recipient</InputLabel>
                  <Select
                    labelId="client-select-label"
                    value={selectedClientId}
                    label="Select Recipient"
                    onChange={(e) => handleClientChange(e.target.value as number)}
                  >
                    <MenuItem value="" disabled>
                      <em>Choose a recipient</em>
                    </MenuItem>
                    {availableClients.map((client) => (
                      <MenuItem key={client.id} value={client.id}>
                        <Box>
                          <Typography variant="body1">
                            {client.name} <Chip label={client.label} size="small" sx={{ ml: 1 }} />
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {availableClients.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    No clients with complete address information found for this funeral.
                  </Alert>
                )}
              </Box>

              {/* Display Selected Client Address */}
              {selectedClient && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Mailing Address:
                  </Typography>
                  <Typography variant="body2">{selectedClient.name}</Typography>
                  <Typography variant="body2">
                    {selectedClient.civic_number ? `${selectedClient.civic_number} ${selectedClient.address}` : selectedClient.address}
                  </Typography>
                  {(selectedClient.apartment || selectedClient.address2) && (
                    <Typography variant="body2">
                      {[
                        selectedClient.apartment ? `Apt ${selectedClient.apartment}` : '',
                        selectedClient.address2 || ''
                      ].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {selectedClient.city}, {selectedClient.province} {selectedClient.postal_code}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Document Selection */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Select Documents to Mail:
                </Typography>

                {availableDocuments.length === 0 ? (
                  <Alert severity="info">
                    No documents available for this funeral.
                  </Alert>
                ) : (
                  <List>
                    {availableDocuments.map((doc, index) => {
                      const isSelected = doc.url in selectedDocuments;
                      return (
                        <ListItem
                          key={index}
                          dense
                          sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            mb: 1,
                            bgcolor: isSelected ? '#f0f7ff' : 'white'
                          }}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              onChange={() => handleDocumentToggle(doc.url)}
                            />
                          </ListItemIcon>
                          <ListItemIcon>
                            <DescriptionIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.filename}
                            secondary={
                              <Chip
                                label={
                                  doc.source === 'contract'
                                    ? 'Signed Contract'
                                    : doc.source === 'cpp_benefits'
                                    ? 'CPP Benefits'
                                    : doc.source === 'verified_document'
                                    ? 'Verified Document'
                                    : 'Attachment'
                                }
                                size="small"
                                sx={{ mt: 0.5 }}
                                color={
                                  doc.source === 'cpp_benefits'
                                    ? 'primary'
                                    : doc.source === 'verified_document'
                                    ? 'success'
                                    : 'default'
                                }
                              />
                            }
                          />
                          {isSelected && (
                            <TextField
                              type="number"
                              label="Copies"
                              size="small"
                              value={selectedDocuments[doc.url]}
                              onChange={(e) => handleQuantityChange(doc.url, parseInt(e.target.value, 10) || 1)}
                              inputProps={{ min: 1, max: 10 }}
                              sx={{ width: 80, ml: 1 }}
                            />
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>

              {selectedUrls.length > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {selectedUrls.length} document(s) selected ({totalCopies} total copies)
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSendMail}
            variant="contained"
            color="primary"
            disabled={!selectedClientId || selectedUrls.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <MailIcon />}
          >
            {loading ? 'Sending...' : 'Send Mail'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PhysicalMailButton;
